import React, { useEffect, useState, useRef } from "react";
import moment from "moment";
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  Typography,
  Fade,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { useHistory, useLocation } from "react-router-dom";
import { Alert, AlertTitle } from "@material-ui/lab";

import {
  OrderModel,
  IOrderWithLocation,
  OrderSortBy,
  OrderSortOrder,
} from "../../models/OrderModel";

export const OrdersView: React.FC = () => {
  const [orders, setOrders] = useState<IOrderWithLocation[]>([]);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortBy, setSortBy] = useState<OrderSortBy>("deliveryAt");
  const [sortOrder, setSortOrder] = useState<OrderSortOrder>("desc");
  const [orderToCancel, setOrderToCancel] = useState<IOrderWithLocation | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showSuccessMessage = queryParams.has("success");
  const showAutoConfirmedMessage = queryParams.get("autoConfirmed") === "true";
  const limit = 10; // Items per page

  const [loading, setLoading] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const loadingTimerRef = useRef<number | null>(null);

  useEffect(() => {
    fetchOrders(page);
  }, [page, sortBy, sortOrder]);

  const fetchOrders = async (currentPage: number) => {
    try {
      setLoading(true);
      loadingTimerRef.current = window.setTimeout(() => setShowLoading(true), 200);

      const data = await OrderModel.getAll(currentPage, limit, sortBy, sortOrder);
      // Orders now carry paymentStatus directly so this list does not need an extra payment join call.
      setOrders(data.orders);
      setTotalCount(data.totalCount);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setShowLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handleSort = (field: OrderSortBy) => {
    // Sorting changes the full paginated result set, so restart at the first page.
    setPage(1);

    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const openCancellation = (order: IOrderWithLocation) => {
    setCancelError("");
    setOrderToCancel(order);
  };

  const closeCancellation = () => {
    if (isCancelling) return;
    setCancelError("");
    setOrderToCancel(null);
  };

  const confirmCancellation = async () => {
    if (!orderToCancel || isCancelling) return;
    try {
      setIsCancelling(true);
      setCancelError("");
      await OrderModel.cancel(orderToCancel._id);
      await fetchOrders(page);
      setOrderToCancel(null);
    } catch (error: any) {
      const responseData = error?.response?.data;
      setCancelError(
        typeof responseData === "string"
          ? responseData
          : typeof responseData?.message === "string"
          ? responseData.message
          : "We could not cancel this delivery. Please try again."
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <Container
      style={{
        minHeight: "75vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Container maxWidth="lg" style={{ marginTop: "3rem", padding: "2rem 0" }}>
        {showSuccessMessage && (
          <Fade in={showSuccessMessage}>
            <Alert severity="success">
              <AlertTitle>Order created!</AlertTitle>
              {showAutoConfirmedMessage
                ? "Your delivery is confirmed. Please check your email or text for the confirmation."
                : "Thank you for scheduling a delivery. We will reach out if we need more information."}
            </Alert>
          </Fade>
        )}

        <Typography variant="h3" style={{ fontSize: "2rem", margin: "1.5rem 0" }}>
          Orders
        </Typography>

        <TableContainer component={Paper}>
          <Table aria-label="orders table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === "createdAt"}
                    direction={sortBy === "createdAt" ? sortOrder : "desc"}
                    onClick={() => handleSort("createdAt")}
                  >
                    Date ordered
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortBy === "deliveryAt"}
                    direction={sortBy === "deliveryAt" ? sortOrder : "desc"}
                    onClick={() => handleSort("deliveryAt")}
                  >
                    Delivery date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Location</TableCell>
                <TableCell align="center">Status</TableCell>
                {/* Payment badge is denormalized on Order and updated by Stripe webhook flow. */}
                <TableCell align="center">Payment</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No Data Available
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order._id}>
                    <TableCell>{moment(order.createdAt).format("ddd, MMMM Do, YYYY")}</TableCell>
                    <TableCell align="center">{moment(order.deliveryAt).format("ddd, MMMM Do, YYYY")}</TableCell>
                    <TableCell align="center">{order.address}</TableCell>
                    <TableCell align="center">
                      {(order.status || "pending").toUpperCase()}
                    </TableCell>
                    <TableCell align="center">
                      {(order.paymentStatus || "pending").toUpperCase()}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" style={{ gap: 8 }}>
                        {order.canCustomerModify && (
                          <Button
                            size="small"
                            color="primary"
                            variant="outlined"
                            onClick={() => history.push(`/modify-delivery/${order._id}`)}
                          >
                            Modify Delivery
                          </Button>
                        )}
                        {order.canCustomerCancel && (
                          <Button
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={() => openCancellation(order)}
                          >
                            Cancel Delivery
                          </Button>
                        )}
                        {!order.canCustomerModify && !order.canCustomerCancel && "—"}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" marginTop="2rem">
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              variant="outlined"
              shape="rounded"
            />
          </Box>
        )}

        <Dialog
          open={Boolean(orderToCancel)}
          onClose={closeCancellation}
          aria-labelledby="cancel-delivery-title"
          aria-describedby="cancel-delivery-description"
        >
          <DialogTitle id="cancel-delivery-title">Cancel Delivery?</DialogTitle>
          <DialogContent>
            <DialogContentText id="cancel-delivery-description">
              {orderToCancel
                ? `Cancel the delivery scheduled for ${moment(orderToCancel.deliveryAt).format(
                    "dddd, MMMM Do, YYYY"
                  )} at ${orderToCancel.address}? This action cannot be undone online.`
                : ""}
            </DialogContentText>
            {cancelError && <Alert severity="error">{cancelError}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCancellation} disabled={isCancelling}>
              Keep Delivery
            </Button>
            <Button
              onClick={confirmCancellation}
              color="secondary"
              variant="contained"
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Confirm Cancellation"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Container>
  );
};
